import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const FREE_SHIPPING_AT = 1999;

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, updateQty, removeItem, subtotal, setCheckoutOpen, customer, setLoginOpen } = useStore();
  const remaining = Math.max(FREE_SHIPPING_AT - subtotal, 0);
  const progress = Math.min((subtotal / FREE_SHIPPING_AT) * 100, 100);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm"
            onClick={() => setCartOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[90] w-full max-w-md bg-cream flex flex-col shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-ink/10">
              <h3 className="font-serif font-bold text-2xl text-ink">Your Basket</h3>
              <button onClick={() => setCartOpen(false)} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="cart-drawer-close" aria-label="Close basket">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-5">
              <div className="bg-mint rounded-2xl p-4" data-testid="cart-shipping-progress">
                <p className="font-mono text-[10px] tracking-widest uppercase text-sage-dark mb-2">
                  {remaining > 0 ? `Add ${inr(remaining)} more for free shipping` : "You've unlocked free shipping!"}
                </p>
                <div className="h-2 bg-white rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-sage rounded-full"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center pt-16" data-testid="cart-empty-state">
                  <ShoppingBag className="w-10 h-10 text-ink/20 mx-auto mb-4" />
                  <p className="font-serif italic text-xl text-charcoal">Your basket is waiting for its first twirl.</p>
                </div>
              ) : (
                cart.map((item) => (
                  <motion.div
                    layout
                    key={item.key}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex gap-4 bg-white rounded-2xl p-3"
                    data-testid={`cart-item-${item.key}`}
                  >
                    <img src={item.image} alt={item.name} className="w-20 h-24 object-cover rounded-xl" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-ink truncate">{item.name}</p>
                      <p className="font-mono text-[10px] text-charcoal/70 mt-0.5">Size {item.size}</p>
                      <p className="font-serif font-bold text-terracotta mt-1">{inr(item.price * item.qty)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQty(item.key, -1)} className="rounded-full border border-ink/15 p-1 hover:border-terracotta" data-testid={`cart-qty-minus-${item.key}`} aria-label="Decrease quantity">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-mono text-xs w-5 text-center">{item.qty}</span>
                        <button onClick={() => updateQty(item.key, 1)} className="rounded-full border border-ink/15 p-1 hover:border-terracotta" data-testid={`cart-qty-plus-${item.key}`} aria-label="Increase quantity">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.key)} className="self-start rounded-full p-1.5 text-charcoal/50 hover:text-terracotta" data-testid={`cart-remove-${item.key}`} aria-label="Remove item">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-ink/10 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-charcoal">Subtotal</span>
                  <span className="font-serif font-bold text-2xl text-ink" data-testid="cart-subtotal">{inr(subtotal)}</span>
                </div>
                <p className="font-mono text-[10px] text-charcoal/60">Shipping calculated at checkout · {remaining > 0 ? inr(99) : "FREE"}</p>
                <button
                  onClick={() => {
                    setCartOpen(false);
                    if (!customer) {
                      setLoginOpen(true);
                      toast.info("Login with a quick OTP to continue to payment");
                      return;
                    }
                    setCheckoutOpen(true);
                  }}
                  className="w-full rounded-full bg-terracotta text-white py-4 text-sm font-semibold hover:bg-terracotta-dark transition-colors duration-300"
                  data-testid="cart-drawer-checkout-btn"
                >
                  Checkout — Guest, 60 seconds
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
